import { merge, type Observable } from 'rxjs'
import type {
  ISchemaBoundValidationContext,
  IValidationSchema,
  IValidator,
} from 'validx'
import { tapAction, toStream } from '@/utils/mobx-util'

/**
 * A view model that validates itself with a schema-bound validation context.
 * Builders, editors and connectors all do.
 */
export interface Validatable {
  readonly validation: ISchemaBoundValidationContext<unknown>
}

/**
 * Validates some of the fields, keeping the errors of the others. A
 * schema-bound context can only validate everything, so the fields' validators
 * run here.
 *
 * @param vm
 * @param schema The schema the view model's validation context is bound to.
 * @param fields
 * @returns Whether the fields are valid.
 */
export function validateFields<T>(
  vm: T & Validatable,
  schema: IValidationSchema<T>,
  fields: (keyof T & string)[],
) {
  let isValid = true
  for (const field of fields) {
    const messages = (schema[field] ?? [])
      .map((validator) => validator({ field, value: vm[field], obj: vm }))
      .filter((result) => result !== true)
      .map((result) => result || 'This field is invalid.')
    vm.validation.clearErrors(field)
    if (messages.length > 0) {
      vm.validation.addErrors({ [field]: messages })
      isValid = false
    }
  }
  return isValid
}

/**
 * Validates each field of the schema whenever it is assigned, while the
 * returned stream is subscribed; pass it to the view model's lifecycle. Fields
 * holding nested view models keep their reference, so only plain fields
 * trigger.
 *
 * @param vm
 * @param schema The schema the view model's validation context is bound to.
 * @param cascades Fields that also validate other fields when they change.
 */
export function validateWhileEditing<T>(
  vm: T & Validatable,
  schema: IValidationSchema<T>,
  cascades: Partial<Record<keyof T & string, (keyof T & string)[]>> = {},
): Observable<unknown> {
  // SAFETY: a schema is keyed by fields of `T`.
  const fields = Object.keys(schema) as (keyof T & string)[]
  return merge(
    ...fields.map((field) =>
      toStream(() => vm[field]).pipe(
        tapAction(() =>
          validateFields(vm, schema, [field, ...(cascades[field] ?? [])]),
        ),
      ),
    ),
  )
}

/**
 * The list must have at least one element.
 *
 * @param message
 */
export function mustHaveItems<T>(message: string): IValidator<T> {
  return function validateHasItems({ value }) {
    // SAFETY: only used on fields holding arrays.
    return (value as unknown[]).length > 0 || message
  }
}

/**
 * The first error of any field, for tab and row indicators.
 *
 * @param validation
 */
export function firstError(
  validation: ISchemaBoundValidationContext<unknown>,
): string | undefined {
  return Object.values(validation.errors).find(
    (messages) => messages.length,
  )?.[0]
}
